import { Box, Divider, Group, rem, Stack, useMantineTheme } from '@mantine/core'
import { useEffect, useState } from 'react'
import { PlayIcon } from '@phosphor-icons/react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { BackButton, PrimaryButton, SecondaryButton } from '~/boggle/ui/atoms/Buttons'
import { resolveBoggleAccentCssColor } from '~/boggle/ui/atoms/boggleAccentColor'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { ChallengeBonusPreview } from '~/boggle/ui/challenge/ChallengeStagingDetails'
import { CompendiumTrophyButton } from '~/boggle/ui/compendium/CompendiumTrophyButton'
import type { BoggleStore } from '~/boggle/ui/state/Store'

const PAUSE_OVERLAY_Z = 35
const BACK_BUTTON_COOLDOWN_MS = 300

export function RunPauseOverlay({
  accentColor = 'yellow.4',
  endRunLabel = 'Exit',
  onBackToChallenge,
  onEndRun,
  onGameRules,
  onRestart,
  resolvedChallenge,
  store,
}: {
  accentColor?: string
  /** Shown when leaving the run from pause (e.g. `Back` for playground settings). */
  endRunLabel?: string
  onBackToChallenge?: () => void
  onEndRun?: () => void
  onGameRules?: () => void
  onRestart: () => void
  resolvedChallenge: BoggleGameState['resolvedChallenge']
  store: BoggleStore
}) {
  const theme = useMantineTheme()
  const [backCooldownDone, setBackCooldownDone] = useState(false)
  useEffect(() => {
    const id = window.setTimeout(() => setBackCooldownDone(true), BACK_BUTTON_COOLDOWN_MS)
    return () => window.clearTimeout(id)
  }, [])
  const restartBorder = `2px solid ${resolveBoggleAccentCssColor(accentColor, theme)}`
  const showBack = onBackToChallenge !== undefined || onEndRun !== undefined

  return (
    <Box
      pos="absolute"
      inset={0}
      style={{
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
        zIndex: PAUSE_OVERLAY_Z,
      }}
    >
      {/*
        Anchor to the viewport like home + run summary: the overlay lives under
        CentralPanel (narrow column); absolute inset-0 would pin the trophy to
        that column's top-right instead of the full run shell.
      */}
      <Box pos="fixed" right={14} top={14} style={{ zIndex: PAUSE_OVERLAY_Z + 1 }}>
        <CompendiumTrophyButton testId="boggle-pause-compendium-trophy" />
      </Box>
      <Stack align="center" flex={1} gap={12} mih={0} miw={0} px={16}>
        <Group align="center" gap={12} mb={30} mt={26} opacity={0.8} pos="relative">
          <Divider
            fz={38}
            fw={400}
            pt={4}
            w={320}
            color="rgba(255,255,255,0.3)"
            label="&nbsp;Paused&nbsp;"
            styles={{
              label: {
                fontSize: rem(34),
              },
            }}
            ta="center"
            role="paragraph"
            tt="uppercase"
          />
        </Group>
        <PrimaryButton
          aria-label="Resume game"
          c="white"
          fw={500}
          fz={24}
          justify="center"
          onClick={() => {
            store.resume()
          }}
          rightSection={
            <Box bottom={-2} left={4} pos="relative">
              <IconGlyph color="white" icon={PlayIcon} size={22} weight="fill" />
            </Box>
          }
          w={210}
        >
          Resume
        </PrimaryButton>
        <SecondaryButton
          bd={restartBorder}
          c="white"
          color="dark.8"
          fz={22}
          h={54}
          onClick={onRestart}
          px={26}
          w={170}
        >
          Restart
        </SecondaryButton>

        {showBack ? (
          <Stack align="center" pb={8} pt={4} px={16}>
            <BackButton
              bdrs={50}
              c="white"
              h={44}
              iconSize={16}
              onClick={() => {
                if (onBackToChallenge) {
                  onBackToChallenge()
                  return
                }
                if (!onEndRun) {
                  return
                }

                // Do not call `store.resume()` here: unpausing runs timers/Pixi before we leave the
                // run shell. `RunVariantShell` already resumes on unmount in its effect cleanup.
                onEndRun()
              }}
              px={26}
              variant="subtle"
              w={160}
              style={{
                pointerEvents: backCooldownDone ? 'auto' : 'none',
              }}
            >
              {endRunLabel}
            </BackButton>
          </Stack>
        ) : null}

        {/* {onGameRules ? (
          <SecondaryButton
            data-testid="boggle-run-pause-game-rules"
            fz={18}
            h={54}
            onClick={onGameRules}
            px={0}
            variant="subtle"
            w={170}
          >
            View Rules
          </SecondaryButton>
        ) : null} */}
        {resolvedChallenge ? (
          <ChallengeBonusPreview bonuses={resolvedChallenge.bonuses} />
        ) : null}
      </Stack>
    </Box>
  )
}
